<template>
    <div
        class="sl-file-uploader"
        :class="{ 'sl-file-uploader--read-only': readOnly }"
    >
        <div v-if="loading" class="flex justify-center items-center w-full">
            <img src="/img/loader.gif" alt="Loading…">
        </div>

        <template v-else>
            <!-- Drop Area -->
            <div
                class="sl-file-uploader__drop-area"
                :class="{ 'sl-file-uploader__drop-area--dragging': dragging }"
                :tabindex="tabIndex"
                @click.self="addBtn_onClick"
                @keydown.enter.self="addBtn_onClick"
                @dragover="dropArea_onDragOver"
                @drop="dropArea_onDrop"
                @dragenter="dragging = true"
                @dragleave="dragging = false"
            >
                <template v-if="!readOnly">
                    {{ t('parameters.UPLOAD_FILES.drop_area.label') }}
                </template>

                <input
                    ref="input"
                    type="file"
                    multiple
                    class="hidden"
                    :disabled="readOnly"
                    @change="input_onChange"
                >

                <!-- Uploaded Files List -->
                <div
                    v-if="uploaded.length"
                    class="sl-file-uploader__files"
                    :class="{ 'pointer-events-none': dragging }"
                >
                    <transition-group :name="`slide-${side}`">
                        <div
                            v-for="file in uploaded"
                            :key="file.name"
                            class="sl-file-uploader__file group"
                        >
                            <!-- File Name -->
                            <div
                                v-tippy
                                :content="file.name"
                                class="sl-file-uploader__name"
                                :class="{
                                    'text-gray-400': downloadingOrUpdating[file.name],
                                    'text-primary-600': !downloadingOrUpdating[file.name],
                                }"
                                :tabindex="tabIndex"
                                @click.stop="downloadFile(file.name)"
                                @keydown.enter.stop="downloadFile(file.name)"
                            >
                                <div>
                                    {{ file.name }}
                                </div>

                                <img
                                    v-if="downloadingOrUpdating[file.name]"
                                    src="/img/loader.gif"
                                    alt="…"
                                    class="block shrink-0 grow-0 my-1 w-3 h-3"
                                />
                            </div>

                            <div class="grow"></div>

                            <!-- File Type -->
                            <template v-if="!typeless && (file.type || !readOnly)">
                                <select
                                    :value="file.type"
                                    class="sl-file-uploader__type"
                                    :class="{
                                        'bg-gray-100 text-gray-400': readOnly || downloadingOrUpdating[file.name],
                                        'text-primary-600': !readOnly && !downloadingOrUpdating[file.name],
                                    }"
                                    :tabindex="tabIndex"
                                    :disabled="readOnly || downloadingOrUpdating[file.name]"
                                    @change="type_onChange(file.name, file.type, $event.target.value)"
                                >
                                    <option v-if="file.type === null" value="">
                                        {{ t('parameters.UPLOAD_FILES.file_type.NULL.label') }}
                                    </option>

                                    <option value="TOPOGRAPHY">
                                        {{ t('parameters.UPLOAD_FILES.file_type.TOPOGRAPHY.label') }}
                                    </option>

                                    <option value="FLUORESCEIN">
                                        {{ t('parameters.UPLOAD_FILES.file_type.FLUORESCEIN.label') }}
                                    </option>

                                    <option value="WHITE_LIGHT">
                                        {{ t('parameters.UPLOAD_FILES.file_type.WHITE_LIGHT.label') }}
                                    </option>

                                    <option value="OTHER">
                                        {{ t('parameters.UPLOAD_FILES.file_type.OTHER.label') }}
                                    </option>
                                </select>
                            </template>

                            <!-- Delete Button -->
                            <div
                                v-if="!readOnly"
                                v-tippy
                                :content="t('common.remove')"
                                class="sl-file-uploader__delete"
                                :tabindex="tabIndex"
                                @click.stop="deleteConfirmationFile = file"
                                @keypress.enter.stop="deleteConfirmationFile = file"
                            >
                                <img
                                    v-if="deleting[file.name]"
                                    src="/img/loader.gif"
                                    alt="…"
                                    class="block shrink-0 grow-0 my-1 w-3 h-3"
                                />

                                <template v-else>
                                    &times;
                                </template>
                            </div>
                        </div>
                    </transition-group>

                    <div v-if="uploading" class="flex justify-center items-center w-full">
                        <img src="/img/loader.gif" alt="">
                    </div>
                </div>
            </div>

            <!-- Delete File Confirmation Modal -->
            <teleport to="body">
                <lsn-modal
                    :is-open="!!deleteConfirmationFile"
                    @keydown.esc="deleteConfirmationFile = null"
                >
                    <div class="sl-prompt sl-prompt--danger">
                        <div class="sl-prompt__contents">
                            <div class="sl-prompt__title">
                                {{ t('parameters.UPLOAD_FILES.delete_confirmation') }}
                            </div>

                            <div class="sl-prompt__text">
                                <span>{{ deleteConfirmationFile.name }}</span>
                            </div>
                        </div>

                        <div class="sl-prompt__actions">
                            <button
                                class="sl-prompt__button sl-prompt__button--white"
                                @click.stop="deleteConfirmationFile = null"
                            >
                                {{ t('common.cancel') }}
                            </button>

                            <button
                                ref="removeBtn"
                                class="sl-prompt__button"
                                :class="{
                                    'sl-prompt__button--gray': deleting[deleteConfirmationFile.name],
                                    'sl-prompt__button--red': !deleting[deleteConfirmationFile.name],
                                }"
                                @click.stop="deleteFile(deleteConfirmationFile.name)"
                            >
                                <img
                                    v-if="deleting[deleteConfirmationFile.name]"
                                    src="/img/loader.gif"
                                    alt="loading…"
                                    class="inline-block align-middle"
                                />

                                <template v-else>
                                    {{ t('common.remove') }}
                                </template>
                            </button>
                        </div>
                    </div>
                </lsn-modal>
            </teleport>
        </template>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import axios from '@/axios';

// ------------------------------------------------------------ PROPS

const props = defineProps({
    modelValue:
    {
        type:    Array,
        default: () => [],
    },

    tabIndex:
    {
        type:    [Number, null],
        default: null,
    },

    side:
    {
        type:    String,
        default: 'right',
    },

    eid:
    {
        type:     String,
        required: true,
    },

    fittingSessionId:
    {
        type:     String,
        required: true,
    },

    readOnly:
    {
        type:    Boolean,
        default: false,
    },

    // Whether type selectors would make no sense
    typeless:
    {
        type:    Boolean,
        default: false,
    },
});

// ------------------------------------------------------------ EMITS

const emit = defineEmits(['update:modelValue']);

// ------------------------------------------------------------ COMPOSABLES

const { t } = useI18n({ useScope: 'global' });
const store = useStore();
const toast = useToast();

// ------------------------------------------------------------ REFS

const input = useTemplateRef('input');

// ------------------------------------------------------------ DATA

/** @type {import("vue").Ref<FileList>} */
const files = ref([]);
const loading = ref(false);
const uploading = ref(false);
/** @type {import("vue").Ref<{name: string, type: ?string, size: number}[]>} */
const uploaded = ref([]);
const downloadingOrUpdating = ref({});
const deleting = ref({});
/** @type {import("vue").Ref<?{name: string, size: number}>} */
const deleteConfirmationFile = ref(null);
const dragging = ref(false);

// ------------------------------------------------------------ COMPUTED

const formData = computed(() =>
{
    const data = new FormData();
    for(let i = 0; i < files.value.length; i++)
    {
        const f = files.value.item(i);
        data.append('files[]', f);
    }

    return data;
});

// ------------------------------------------------------------ METHODS

/**
 * Open the file selection dialog.
 *
 * @param {Event} e
 */
function addBtn_onClick(e)
{
    e.preventDefault();
    if(uploading.value)
    {
        return;
    }

    input.value.click();
}

/**
 * Disable the default dragover handler, to allow handling the `drop` event.
 *
 * @param {DragEvent} e
 */
function dropArea_onDragOver(e)
{
    e.preventDefault();
}

/**
 * Handle files being dropped onto the drop area.
 *
 * @param {DragEvent} e
 */
async function dropArea_onDrop(e)
{
    e.preventDefault();
    dragging.value = false;

    if(props.readOnly)
    {
        return;
    }

    // Send the dropped files to the input
    const dt = new DataTransfer();
    [...e.dataTransfer.items]
        .map(i => i.getAsFile())
        .filter(f => f)
        .forEach(f => dt.items.add(f));

    input.value.files = dt.files;
    await input_onChange();
}

/**
 * Upload newly selected files to the back-end.
 */
async function input_onChange()
{
    if(props.readOnly)
    {
        return;
    }

    files.value = input.value.files;

    // Upload the files
    try
    {
        const data = await upload();
        for(const f of data)
        {
            if(props.typeless)
            {
                f.type = null;
            }

            uploaded.value.push(f);
        }
    }
    catch(error)
    {
        console.error('could not upload files', {
            files: files.value,
            error,
        });

        if(error instanceof Error && error.name === 'AlreadyUploadingError')
        {
            toast.error(t('common.errors.already_uploading'));
            return;
        }

        toast.error(t('common.errors.error'));
    }
}

async function type_onChange(filename, oldType, newType)
{
    if(props.typeless)
    {
        newType = null;
    }

    const file = uploaded.value.find(f => f.name === filename);

    if(downloadingOrUpdating.value[filename])
    {
        file.type = oldType;
        return;
    }

    downloadingOrUpdating.value[filename] = true;

    const url = '/api/fitting-session/{eid}/{id}/file/{name}'
        .replace('{eid}', props.eid)
        .replace('{id}', props.fittingSessionId)
        .replace('{name}', filename);

    try
    {
        await axios.patch(url, { type: newType });
        file.type = newType;
    }
    catch(error)
    {
        console.error('could not update type', {
            filename,
            oldType,
            newType,
            error,
        });
        toast.error(t('common.errors.error'));
        file.type = oldType;
    }
    finally
    {
        delete downloadingOrUpdating.value[filename];
    }
}

/**
 * Upload the files to the back-end.
 *
 * @async
 * @returns {Promise<{name: string, type: ?string, size: number}[]>}
 */
async function upload()
{
    if(uploading.value)
    {
        const error = new Error('Already uploading');
        error.name = 'AlreadyUploadingError';
        throw error;
    }

    uploading.value = true;

    const url = '/api/fitting-session/{eid}/{id}/file'
        .replace('{eid}', props.eid)
        .replace('{id}', props.fittingSessionId);
    const config = { headers: { 'Content-Type': 'multipart/form-data' } };

    try
    {
        const { data } = await axios.post(url, formData.value, config);
        if(props.typeless)
        {
            data.type = null;
        }
        return data;
    }
    catch(error)
    {
        throw error;
    }
    finally
    {
        uploading.value = false;
    }
}

function formatSize(size)
{
    const kB = 1024;
    const MB = kB ** 2;
    if(size >= MB)
    {
        return (size / MB).toFixed(2) + ' MB';
    }

    return (size / kB).toFixed(2) + ' kB';
}

async function downloadFile(name)
{
    if(downloadingOrUpdating.value[name])
    {
        return;
    }

    // Download the file through a guarded API route
    downloadingOrUpdating.value[name] = true;
    const url = '/api/fitting-session/{eid}/{id}/file/{name}'
        .replace('{eid}', props.eid)
        .replace('{id}', props.fittingSessionId)
        .replace('{name}', name);

    try
    {
        const response = await axios.get(url, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const href = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = name;
        a.click();
    }
    catch(error)
    {
        console.error('could not download file', { name, error });
        toast.error(t('parameters.UPLOAD_FILES.errors.cannot_download_file'));
    }
    finally
    {
        delete downloadingOrUpdating.value[name];
    }
}

async function deleteFile(name)
{
    if(downloadingOrUpdating.value[name] || deleting.value[name])
    {
        return;
    }

    deleting.value[name] = true;
    const url = '/api/fitting-session/{eid}/{id}/file/{name}'
        .replace('{eid}', props.eid)
        .replace('{id}', props.fittingSessionId)
        .replace('{name}', name);

    try
    {
        await axios.delete(url);

        // Remove the file from the local list
        const index = uploaded.value.findIndex(f => f.name === name);
        if(index !== -1)
        {
            uploaded.value.splice(index, 1);
        }

        // Close the confirmation dialog
        deleteConfirmationFile.value = null;
    }
    catch(error)
    {
        console.error('could not delete file', { name, error });
        toast.error(t('parameters.UPLOAD_FILES.errors.cannot_delete_file'));
    }
    finally
    {
        delete deleting.value[name];
    }
}

function updateModelValue()
{
    const files = uploaded.value.map(f =>
    {
        const file = { name: f.name, type: f.type };
        if(props.typeless)
        {
            file.type = null;
        }

        return file;
    });

    emit('update:modelValue', files);
}

// ------------------------------------------------------------ WATCHERS

watch(() => uploaded.value, updateModelValue, { deep: true });
watch(() => props.typeless, async() =>
{
    // If the component becomes typeless, reset all previous file types
    if(!props.typeless)
    {
        return;
    }

    for(const f of uploaded.value)
    {
        if(f.type === null)
        {
            continue;
        }

        await type_onChange(f.name, f.type, null);
    }
});

// ------------------------------------------------------------ LIFECYCLE HOOKS

onMounted(async() =>
{
    // Preload the list of previously uploaded files
    loading.value = true;

    const url = '/api/fitting-session/{eid}/{id}/file'
        .replace('{eid}', props.eid)
        .replace('{id}', props.fittingSessionId);

    try
    {
        const uploads = [];
        const { data } = await axios.get(url);
        for(const f of data)
        {
            if(props.typeless)
            {
                f.type = null;
            }
            uploads.push(f);
        }
        uploaded.value = uploads;
    }
    catch(error)
    {
        console.error('could not preload file list', { error });
        toast.error(t('parameters.UPLOAD_FILES.errors.cannot_load_files'));
    }
    finally
    {
        loading.value = false;
    }
});
</script>

<style scoped>
@reference "#main.css";

@layer components
{
    .sl-file-uploader
    {
        @apply flex flex-col w-full;
    }

    .sl-file-uploader--read-only
    {
    }

    .sl-file-uploader__drop-area
    {
        @apply cursor-pointer flex flex-col justify-start items-center
            border border-dashed border-gray-400 rounded
            w-full p-4
            bg-white text-gray-600 text-sm
            select-none outline-none transition
            hover:border-primary-400 focus-visible:border-primary-400
            focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-primary-500;
    }

    .sl-file-uploader--read-only .sl-file-uploader__drop-area
    {
        @apply cursor-default border-none p-0;
    }

    .sl-file-uploader__drop-area--dragging
    {
        @apply bg-primary-100;
    }

    .sl-file-uploader--read-only .sl-file-uploader__drop-area--dragging
    {
        @apply bg-white;
    }

    .sl-file-uploader__files
    {
        @apply flex flex-col gap-1 mt-2 w-full;
    }

    .sl-file-uploader--read-only .sl-file-uploader__files
    {
        @apply mt-0;
    }

    .sl-file-uploader__file
    {
        @apply cursor-default flex justify-start items-center gap-1 rounded-md p-1
            w-full bg-gray-100;
    }

    .sl-file-uploader__name
    {
        @apply cursor-pointer flex items-center gap-1 px-1 rounded
            text-sm truncate select-none outline-none
            focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-primary-500;
    }

    .sl-file-uploader__type
    {
        @apply cursor-pointer rounded border border-gray-300 px-2 py-1
            bg-white text-sm select-none outline-none
            focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-primary-500;
    }

    .sl-file-uploader--read-only .sl-file-uploader__type
    {
        @apply cursor-default appearance-none text-gray-500;
    }

    .sl-file-uploader__delete
    {
        @apply cursor-pointer inline-block rounded px-1 text-red-500
            grayscale outline-none transition-all
            hover:text-red-600
            focus:text-red-600 focus:grayscale-0
            focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-red-500
            group-hover:grayscale-0;
    }
}
</style>
